/**
 * PresenceOrb v2 — "Companion" (LUM-133 / LUM-183 design pass)
 *
 * Chosen from a 22-variant exploration (design/orb-explorations/):
 * a navy dot and a small orange spark whose RELATIONSHIP is the state —
 * we read the pair socially, so the states need no decoding:
 *
 *   listening — spark rests close at the dot's lower-left, both
 *               breathing in phase ("with you — take your time")
 *   thinking  — spark departs to orbit a hairline track (visibly
 *               working; orbit-as-processing was the exploration's
 *               strongest zero-explanation read)
 *   speaking  — spark docks at the dot's right; the two trade beats
 *               half a cycle apart (alternation = utterance)
 *
 * A quiet state word rides beside the mark ("listening · take your
 * time" / "thinking…" / "speaking") — the LUM-183 research found no
 * shipped voice product that trusts an abstract mark alone, and the
 * listening copy does the orb's primary job (licensing silence) in
 * words. The recording consent marker is deliberately GONE from the
 * chrome: recording is always on, so a live indicator misleads —
 * consent lives in the product copy instead (review decision, LUM-183).
 *
 * Animations are deterministic CSS in styles.css; the box never
 * resizes, so no layout shift. prefers-reduced-motion collapses to
 * statically distinct poses (spark station + track visibility carry
 * the state without motion); the JS hook mirrors the media query so
 * the branch is unit-testable in jsdom.
 */

import { Group, Stack, Text } from '@mantine/core';
import { useReducedMotion } from '@mantine/hooks';
import type { RealtimeAgentState } from '@/services/OpenAIRealtimeService';

export interface PresenceOrbProps {
  state: RealtimeAgentState;
  /** `row` (default) sits inline in a title row; `stage` is the in-call
   *  centrepiece (LUM-185 Stage layout): mark scaled ~3.4x with the
   *  state word beneath it. Geometry scales via the --ps custom
   *  property in styles.css. */
  variant?: 'row' | 'stage';
  /** The call is spinning up (LUM-185 harmonised flow): the stage is
   *  already showing but nothing is live yet. Presents the listening
   *  pose with "connecting…" — a display word, not an agent state. */
  connecting?: boolean;
}

// Announced via aria-label; the visible word is decorative alongside.
const STATE_LABELS: Record<RealtimeAgentState, string> = {
  listening: 'Interviewer is listening',
  thinking: 'Interviewer is thinking',
  speaking: 'Interviewer is speaking',
};

// The visible state word. Listening carries the silence licence in
// plain register (tester feedback: "can't tell if it's waiting for me").
const STATE_WORDS: Record<RealtimeAgentState, string> = {
  listening: 'listening · take your time',
  thinking: 'thinking…',
  speaking: 'speaking',
};

export function PresenceOrb({
  state,
  variant = 'row',
  connecting = false,
}: Readonly<PresenceOrbProps>) {
  const reducedMotion = useReducedMotion();

  const orbClassName = [
    'presence-pair',
    reducedMotion ? 'presence-pair--static' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const pose: RealtimeAgentState = connecting ? 'listening' : state;
  const label = connecting ? 'Interviewer is connecting' : STATE_LABELS[state];
  const word = connecting ? 'connecting…' : STATE_WORDS[state];

  const mark = (
    <div
      role="img"
      aria-label={label}
      data-state={pose}
      className={orbClassName}
      style={variant === 'stage' ? { ['--ps' as string]: 3.4 } : undefined}
    >
      <span className="presence-pair-track" />
      <span className="presence-pair-dot" />
      <span className="presence-pair-sat" />
    </div>
  );

  if (variant === 'stage') {
    return (
      <Stack gap={14} align="center">
        {mark}
        <Text fz={17} c="navy.8" style={{ letterSpacing: '0.02em' }}>
          {word}
        </Text>
      </Stack>
    );
  }

  return (
    // alignSelf centers the unit inside the page's baseline-aligned
    // title row — the mark has no text baseline to align to.
    <Group gap={8} align="center" style={{ alignSelf: 'center' }}>
      {mark}
      <Text fz={13} c="navy.8" style={{ letterSpacing: '0.02em' }}>
        {word}
      </Text>
    </Group>
  );
}
